import { DateTime, Duration } from 'luxon';
import { SettingsState, CultureState } from '@/store';
import { TimeSpan } from '.';

export class ClientDate {

    public static compare(
        l: ClientDate | null | undefined,
        r: ClientDate | null | undefined
    ): number {

        let result: number;

        if (typeof(l) === 'undefined' && typeof(r) === 'undefined') {
            result = 0;
        } else if (typeof(l) === 'undefined') {
            result = 1;
        } else if (typeof(r) === 'undefined') {
            result = -1;
        } else

        if (l === null && r === null) {
            result = 0;
        } else if (l === null) {
            result = 1;
        } else if (r === null) {
            result = -1;
        } else {
            const lms = l.date.toMillis();
            const rms = r.date.toMillis();

            if (lms < rms) {
                result = -1;
            } else if (lms > rms) {
                result = 1;
            } else {
                result = 0;
            }
        }

        return result;
    }

    public static duration(start: ClientDate, end: ClientDate): TimeSpan {
        return new TimeSpan(end.date.diff(start.date));
    }

    private static getUserUTCZone(): string {
        const clientOffset = SettingsState.contextSettings.global.timeOffset || 0;
        const strClientOffset = clientOffset >= 0
            ? `+${clientOffset}`
            : `${clientOffset}`;
        const result = `UTC${strClientOffset}`;
        return result;
    }

    private static ctorNow(): DateTime {
        const utcOffset = ClientDate.getUserUTCZone();
        const jsNow = new Date();
        const result = DateTime.fromJSDate(jsNow, { zone: utcOffset});
        return result;
    }
    private static ctorDateTime(date: DateTime): DateTime {
        return date;
    }
    private static ctorString(value: string): DateTime {
        const utcOffset = ClientDate.getUserUTCZone();
        return DateTime.fromISO(value, { setZone: true, }).setZone(utcOffset);
    }

    private readonly date: DateTime;

    constructor(date?: DateTime | string) {
        switch (arguments.length) {
            case 0:
                this.date = ClientDate.ctorNow();
                break;
            case 1:
                if (typeof arguments[0] === 'string') {
                    this.date = ClientDate.ctorString(arguments[0]);
                } else {
                    this.date = ClientDate.ctorDateTime(arguments[0]);
                }
                break;
            default:
                throw new Error(`${arguments.length} arguments overload is not supported`);
        }
    }

    public onlyDate(): ClientDate {
        const date = new ClientDate(this.date.startOf('day'));
        return date;
    }
    public equals(date: ClientDate): boolean {
        return this.date.equals(date.date);
    }

    public addDay(value: number): ClientDate {
        const duration = Duration.fromMillis(value * 24 * 60 * 60 * 1000);
        return this.addDuration(duration);
    }
    public addHour(value: number): ClientDate {
        const duration = Duration.fromMillis(value * 60 * 60 * 1000);
        return this.addDuration(duration);
    }

    public addTimeSpan(timeSpan: TimeSpan): ClientDate {
        const duration = timeSpan.duration;
        return this.addDuration(duration);
    }

    public toDate(): Date {
        return this.date.toJSDate();
    }

    public toDateString(format?: string): string {

        if (!format || format.length === 0) {
            format = SettingsState.contextSettings.global.formatDate || 'yyyy-MM-dd';
        }

        const culture = CultureState.currentCulture;
        let localeDate: DateTime;
        if (!culture || !culture.locale) {
            localeDate = this.date;
        } else {
            localeDate = this.date.setLocale(culture.locale);
        }

        const result = localeDate.toFormat(format);

        return result;
    }

    public toDateTimeString(format?: string): string {

        if (!format || format.length === 0) {
            const formatDate = SettingsState.contextSettings.global.formatDate || 'yyyy-MM-dd';
            const formatTime = SettingsState.contextSettings.global.formatTime || 'HH:mm:ss';
            format = `${formatDate} ${formatTime}`;
        }

        const culture = CultureState.currentCulture;
        let localeDate: DateTime;
        if (!culture || !culture.locale) {
            localeDate = this.date;
        } else {
            localeDate = this.date.setLocale(culture.locale);
        }

        const result = localeDate.toFormat(format);

        return result;
    }

    public toQueryParameter(): string {
        const utcDate = this.date.toUTC();
        const result = `${utcDate.toISO()}`;
        return result;
    }

    private addDuration(duration: Duration): ClientDate {
        const changedDate = this.date.plus(duration);
        const result = new ClientDate(changedDate);
        return result;
    }

}
